/**
 * The Inbox: the visitor's converted files (GET /api/v1/downloads), with
 * sorting, selection, deletion and the "limits" dialog that free visitors see
 * before a download (the files of free conversions are trial versions).
 * Every download counts as the analytics event download_any.
 */

import { trackEvent } from "./analytics";
import { deleteDownload, listDownloads } from "./api/client";
import type { Download } from "./api/types";
import { announce } from "./lib/announce";
import { dialogById, openDialog, closeDialog } from "./lib/dialog";
import { find, findAll, get, h } from "./lib/dom";
import { extensionOf, formatDateTime, formatSize } from "./lib/format";
import { errorMessage, isAbortError } from "./lib/http";

type SortKey = "name" | "size" | "date";
interface SortOrder {
  readonly key: SortKey;
  readonly descending: boolean;
}

const DELETE_CONCURRENCY = 3;
const ICONS: Readonly<Record<string, string>> = {
  doc: "/img/mime_doc.png",
  docx: "/img/mime_doc.png",
  rtf: "/img/mime_doc.png",
  pdf: "/img/mime_pdf.png",
  zip: "/img/mime_jpg.png",
  jpg: "/img/mime_jpg.png",
  xls: "/img/mime_xls.png",
  xlsx: "/img/mime_xls.png",
};

/** The downloads in the given order (newest first by default). */
export function sortDownloads(
  downloads: readonly Download[],
  order: SortOrder,
): Download[] {
  const direction = order.descending ? -1 : 1;
  const compare = (a: Download, b: Download): number => {
    switch (order.key) {
      case "name":
        return a.name.localeCompare(b.name);
      case "size":
        return a.size - b.size;
      case "date":
        return a.createdAt.getTime() - b.createdAt.getTime();
    }
  };
  return downloads.toSorted((a, b) => direction * compare(a, b));
}

/** The next order after a click on a column header. */
export function nextOrder(current: SortOrder, key: SortKey): SortOrder {
  if (current.key === key) return { key, descending: !current.descending };
  return { key, descending: key !== "name" };
}

function isSortKey(value: string | undefined): value is SortKey {
  return value === "name" || value === "size" || value === "date";
}

/** Run `task` for every item, at most `limit` at a time; never rejects. */
async function settleAll<T>(
  items: readonly T[],
  limit: number,
  task: (item: T) => Promise<void>,
): Promise<number> {
  let next = 0;
  let failures = 0;
  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const item = items[next];
      next += 1;
      if (item === undefined) continue;
      try {
        await task(item);
      } catch {
        failures += 1; // counted and reported by the caller
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker),
  );
  return failures;
}

export class Inbox {
  private downloads: Download[] = [];
  private order: SortOrder = { key: "date", descending: true };
  private readonly selected = new Set<string>();
  private loading: AbortController | null = null;
  private loadedOnce = false;
  private readonly rows: HTMLTableSectionElement;
  private readonly selectAll: HTMLInputElement;
  private readonly deleteButton: HTMLButtonElement;
  private readonly refreshButton: HTMLButtonElement;
  private readonly standalone: boolean;
  private pendingDelete: readonly string[] = [];
  private readonly root: HTMLElement;
  private readonly isPremium: () => boolean;

  constructor(root: HTMLElement, isPremium: () => boolean) {
    this.root = root;
    this.isPremium = isPremium;
    this.rows = get(root, "[data-inbox-rows]", HTMLTableSectionElement);
    this.selectAll = get(root, "[data-inbox-select-all]", HTMLInputElement);
    this.deleteButton = get(root, "[data-inbox-delete]", HTMLButtonElement);
    this.refreshButton = get(root, "[data-inbox-refresh]", HTMLButtonElement);
    this.standalone = root.hasAttribute("data-inbox-standalone");
    this.bind();
  }

  private bind(): void {
    this.refreshButton.addEventListener("click", () => void this.refresh());
    find(this.root, "[data-inbox-retry]", HTMLButtonElement)?.addEventListener(
      "click",
      () => void this.refresh(),
    );
    this.selectAll.addEventListener("change", () => {
      this.selected.clear();
      if (this.selectAll.checked) {
        for (const download of this.downloads) this.selected.add(download.id);
      }
      this.render();
    });
    this.deleteButton.addEventListener("click", () => {
      this.confirmDelete([...this.selected]);
    });
    for (const button of findAll(this.root, "[data-sort]", HTMLButtonElement)) {
      button.addEventListener("click", () => {
        const key = button.dataset["sort"];
        if (!isSortKey(key)) return;
        this.order = nextOrder(this.order, key);
        this.render();
      });
    }
    this.rows.addEventListener("click", (event) => {
      this.onRowClick(event);
    });
    this.rows.addEventListener("change", (event) => {
      const box = event.target;
      if (!(box instanceof HTMLInputElement)) return;
      const id = box.dataset["id"];
      if (id === undefined) return;
      if (box.checked) this.selected.add(id);
      else this.selected.delete(id);
      this.updateSelection();
    });
    const dialog = dialogById("delete-dialog");
    if (dialog !== null) {
      find(
        dialog,
        "[data-delete-confirm]",
        HTMLButtonElement,
      )?.addEventListener("click", () => void this.deletePending(dialog));
    }
    const limits = dialogById("limits-dialog");
    if (limits !== null) {
      find(
        limits,
        "[data-download-anyway]",
        HTMLAnchorElement,
      )?.addEventListener("click", () => {
        trackEvent("download_any");
      });
    }
  }

  /** Reload the list (a newer refresh cancels an older one). Never rejects: failures are shown. */
  async refresh(): Promise<void> {
    this.loading?.abort();
    const controller = new AbortController();
    this.loading = controller;
    this.refreshButton.disabled = true;
    this.showState(this.loadedOnce ? "content" : "loading");
    try {
      this.downloads = await listDownloads(controller.signal);
      this.loadedOnce = true;
      const ids = new Set(this.downloads.map((download) => download.id));
      for (const id of [...this.selected]) {
        if (!ids.has(id)) this.selected.delete(id);
      }
      this.render();
    } catch (error) {
      if (isAbortError(error)) return;
      this.showError(errorMessage(error));
    } finally {
      if (this.loading === controller) {
        this.loading = null;
        this.refreshButton.disabled = false;
      }
    }
  }

  private showState(
    state: "loading" | "empty" | "error" | "content" | "none",
  ): void {
    const set = (selector: string, visible: boolean): void => {
      const element = find(this.root, selector, HTMLElement);
      if (element) element.hidden = !visible;
    };
    set("[data-inbox-loading]", state === "loading");
    set("[data-inbox-empty]", state === "empty");
    set("[data-inbox-error]", state === "error");
    set("[data-inbox-content]", state === "content");
    // on converter pages the whole block appears only with files (or a problem)
    if (!this.standalone) {
      this.root.hidden =
        state === "none" || state === "loading" || state === "empty";
    }
  }

  private showError(message: string): void {
    const text = find(this.root, "[data-inbox-error-text]", HTMLElement);
    if (text) text.textContent = message;
    this.showState("error");
  }

  private render(): void {
    const count = this.downloads.length;
    showInboxCount(count);
    if (count === 0) {
      this.showState(this.standalone ? "empty" : "none");
      this.rows.replaceChildren();
      this.updateSelection();
      return;
    }
    this.showState("content");
    this.rows.replaceChildren(
      ...sortDownloads(this.downloads, this.order).map((download) =>
        this.row(download),
      ),
    );
    for (const header of findAll(
      this.root,
      "[data-sort-column]",
      HTMLElement,
    )) {
      const key = header.dataset["sortColumn"];
      header.setAttribute(
        "aria-sort",
        key === this.order.key
          ? this.order.descending
            ? "descending"
            : "ascending"
          : "none",
      );
    }
    this.updateSelection();
  }

  private row(download: Download): HTMLTableRowElement {
    const icon = ICONS[extensionOf(download.name)];
    const checkbox = h("input", {
      attrs: {
        type: "checkbox",
        "data-id": download.id,
        "aria-label": `Select ${download.name}`,
      },
    });
    checkbox.checked = this.selected.has(download.id);
    const name = h(
      "td",
      { className: "inbox-table__name" },
      ...(icon === undefined
        ? []
        : [
            h("img", {
              attrs: { src: icon, alt: "", width: "16", height: "16" },
            }),
          ]),
      h("span", {
        className: "inbox-table__file",
        text: download.name,
        attrs: { title: download.name },
      }),
    );
    const link = h(
      "a",
      {
        className: "btn btn--small",
        attrs: {
          href: download.url,
          download: download.name,
          "data-download": download.url,
        },
      },
      "Download",
      h("span", { className: "visually-hidden", text: ` ${download.name}` }),
    );
    const remove = h("button", {
      className: "icon-button",
      text: "×",
      attrs: {
        type: "button",
        "data-delete": download.id,
        "aria-label": `Delete ${download.name}`,
      },
    });
    return h(
      "tr",
      {},
      h("td", { className: "inbox-table__check" }, checkbox),
      name,
      h("td", {
        className: "inbox-table__size",
        text: formatSize(download.size),
      }),
      h(
        "td",
        { className: "inbox-table__date" },
        h("time", {
          text: formatDateTime(download.createdAt),
          attrs: { datetime: download.createdAt.toISOString() },
        }),
      ),
      h("td", { className: "inbox-table__action" }, link),
      h("td", { className: "inbox-table__action" }, remove),
    );
  }

  private updateSelection(): void {
    const total = this.downloads.length;
    const chosen = this.selected.size;
    this.selectAll.checked = total > 0 && chosen === total;
    this.selectAll.indeterminate = chosen > 0 && chosen < total;
    this.deleteButton.disabled = chosen === 0;
  }

  private onRowClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const remove = target.closest("[data-delete]");
    if (remove instanceof HTMLElement) {
      const id = remove.dataset["delete"];
      if (id !== undefined) this.confirmDelete([id]);
      return;
    }
    const link = target.closest("[data-download]");
    if (!(link instanceof HTMLAnchorElement)) return;
    if (!this.isPremium() && this.warnBeforeDownload(link)) {
      event.preventDefault(); // the dialog's "Download anyway" downloads (and counts)
      return;
    }
    trackEvent("download_any");
  }

  /** Free conversions are trial versions: say so before the download. Whether the dialog opened. */
  private warnBeforeDownload(link: HTMLAnchorElement): boolean {
    const dialog = dialogById("limits-dialog");
    const anyway =
      dialog && find(dialog, "[data-download-anyway]", HTMLAnchorElement);
    if (!dialog || !anyway) return false;
    anyway.setAttribute("href", link.getAttribute("href") ?? "/inbox");
    anyway.setAttribute("download", link.getAttribute("download") ?? "");
    openDialog(dialog);
    return true;
  }

  private confirmDelete(ids: readonly string[]): void {
    const dialog = dialogById("delete-dialog");
    if (dialog === null || ids.length === 0) return;
    this.pendingDelete = ids;
    const name = find(dialog, "[data-delete-name]", HTMLElement);
    if (name) {
      const single =
        ids.length === 1
          ? this.downloads.find((d) => d.id === ids[0])
          : undefined;
      name.textContent =
        single?.name ?? `${ids.length} ${ids.length === 1 ? "file" : "files"}`;
    }
    const confirm = find(dialog, "[data-delete-confirm]", HTMLButtonElement);
    if (confirm) confirm.disabled = false;
    openDialog(dialog);
  }

  private async deletePending(dialog: HTMLDialogElement): Promise<void> {
    const ids = this.pendingDelete;
    this.pendingDelete = [];
    if (ids.length === 0) return;
    const confirm = find(dialog, "[data-delete-confirm]", HTMLButtonElement);
    if (confirm) confirm.disabled = true;
    const failures = await settleAll(ids, DELETE_CONCURRENCY, async (id) => {
      await deleteDownload(id);
      this.selected.delete(id);
    });
    closeDialog(dialog);
    announce(
      failures === 0
        ? `${ids.length} ${ids.length === 1 ? "file" : "files"} deleted.`
        : "Some files could not be deleted. Please try again.",
    );
    await this.refresh();
    // the row that held the focus is gone: keep the keyboard user in the inbox
    if (
      document.activeElement === document.body ||
      document.activeElement === null
    ) {
      (this.root.hidden
        ? document.getElementById("main")
        : this.refreshButton
      )?.focus();
    }
  }
}

/** The number of the visitor's files on the "Inbox" links of the page (hidden at zero). */
function showInboxCount(count: number): void {
  for (const badge of findAll(document, "[data-inbox-count]", HTMLElement)) {
    badge.textContent = String(count);
    badge.hidden = count === 0;
  }
}

/** Only the count, for pages without the inbox block. Never rejects: the count is optional. */
async function countFiles(): Promise<void> {
  try {
    showInboxCount((await listDownloads()).length);
  } catch {
    // without an answer the link simply shows no number
  }
}

/** Start the inbox of the page (tool pages and /inbox); other pages only count the files for the menu link. */
export function initInbox(isPremium: () => boolean): Inbox | null {
  const root = find(document, "[data-inbox]", HTMLElement);
  if (root === null) {
    void countFiles();
    return null;
  }
  const inbox = new Inbox(root, isPremium);
  void inbox.refresh();
  return inbox;
}
